import { CommonSuggestionFn, commonSuggestions } from "./commonSuggestions";
import { DateTimeSuggestionVariables } from "./DateTimeSuggestion";
import { getStartOfWeek, getDaysSinceWeekday, Weekday } from "./utils";
import { DateTime } from "luxon";

/**
 * - "Before" options should be interpreted as "before the start of the period"
 *   - e.g. "due before this week" -> "before first day of week at 12am"
 *
 * - "After" options should be interpreted as "after the end of the period"
 *   - e.g. "due after this week" -> "after last day of week at 11:59pm"
 */
// Note that the suggestion ID must match the suggestion's key in the dueDateRuleSuggestions
// object below. Additionally, suggestion information is persisted as the `RelativeDueDate`
// type in the `schema.ts` file. If the suggestion ID changes or if the content/contentVariables
// associated with a suggestion changes then existing logic will break.
// - Persisted in inbox subsection rules
// - Persisted in user settings
export const dueDateRuleSuggestions = {
  today: commonSuggestions.today,
  todayAtNumber: commonSuggestions.todayAtNumber,
  todayAtTime: commonSuggestions.todayAtTime,

  tomorrow: commonSuggestions.tomorrow,
  tomorrowAtNumber: commonSuggestions.tomorrowAtNumber,
  tomorrowAtTime: commonSuggestions.tomorrowAtTime,

  thisWeek: commonSuggestions.thisWeek,
  thisWeekend: commonSuggestions.thisWeekend,
  thisMonth: commonSuggestions.thisMonth,
  thisYear: commonSuggestions.thisYear,

  nextWeek: commonSuggestions.nextWeek,
  // nextWeekend: commonSuggestions.nextWeekend,
  nextMonth: commonSuggestions.nextMonth,
  nextYear: commonSuggestions.nextYear,

  anHourFromNow: commonSuggestions.anHourFromNow,
  aDayFromNow: commonSuggestions.aDayFromNow,
  aWeekFromNow: commonSuggestions.aWeekFromNow,
  aMonthFromNow: commonSuggestions.aMonthFromNow,

  xHoursFromNow: commonSuggestions.xHoursFromNow,
  xDaysFromNow: commonSuggestions.xDaysFromNow,
  xWeeksFromNow: commonSuggestions.xWeeksFromNow,
  // xMonthsFromNow: commonSuggestions.xMonthsFromNow,
} satisfies Record<string, CommonSuggestionFn>;

export type DueDateRuleInflectionPointProps = {
  notificationDue: DateTime<true>;
  contentVariables: DateTimeSuggestionVariables;
  weekStartsOn: "su" | "mo";
  weekendStartsOn: "sa";
};

export type DueDateRuleInflectionPointResolver = (
  props: DueDateRuleInflectionPointProps,
) => DateTime<true> | DateTime<false>;

export const dueBeforeRuleInflectionPoint = {
  // - notification due on Sun, 3/30 & 5pm
  // - "due before today"
  // - template: "today"
  // - reindex on Mon, 3/31 at 12am
  //   - interpreted as due before Mon, 3/31 at 12am
  today: (props) => {
    const { notificationDue } = props;
    return notificationDue.plus({ days: 1 }).startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before today at 3pm"
  // - template: "today at {{NUMBER}}" coerced/normalized to "today at {{TIME}}"
  // - reindex on Mon, 3/31 at 12am
  //   - interpreted as due before Mon, 3/31 at 3pm
  todayAtNumber: (props) => {
    const { time } = props.contentVariables;

    if (!time) {
      throw new Error("[dueBeforeRuleInflectionPoint] [todayAtNumber] time content variable is required");
    }

    // We need this type assertion so that `dueBeforeRuleInflectionPoint`'s type doesn't
    // reference itself.
    const resolver = dueBeforeRuleInflectionPoint.todayAtTime as DueDateRuleInflectionPointResolver;
    return resolver(props);
  },
  // - template: "today at {{TIME}}"
  todayAtTime: (props) => {
    const { notificationDue, contentVariables } = props;
    const { time } = contentVariables;

    if (!time) {
      throw new Error("[dueBeforeRuleInflectionPoint] [todayAtTime] time content variable is required");
    }

    const sameDayAtTime = notificationDue.set({ hour: time.hours, minute: time.minutes }).startOf("minute");

    if (notificationDue < sameDayAtTime) return notificationDue.startOf("day");

    return notificationDue.plus({ days: 1 }).startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before tomorrow"
  // - template: "tomorrow"
  // - reindex on Sun, 3/30 at 12am
  //   - interpreted as due before Mon, 3/31 at 12am
  tomorrow: (props) => {
    const { notificationDue } = props;
    return notificationDue.startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before tomorrow at 3pm"
  // - template: "tomorrow at {{NUMBER}}" coerced/normalized to "tomorrow at {{TIME}}"
  // - reindex on Sun, 3/30 at 12am
  //   - interpreted as due before Mon, 3/31 at 3pm
  tomorrowAtNumber: (props) => {
    const { time } = props.contentVariables;

    if (!time) {
      throw new Error("[dueBeforeRuleInflectionPoint] [tomorrowAtNumber] time content variable is required");
    }

    // We need this type assertion so that `dueBeforeRuleInflectionPoint`'s type doesn't
    // reference itself.
    const resolver = dueBeforeRuleInflectionPoint.tomorrowAtTime as DueDateRuleInflectionPointResolver;
    return resolver(props);
  },
  // - template: "tomorrow at {{TIME}}"
  tomorrowAtTime: (props) => {
    const { notificationDue, contentVariables } = props;
    const { time } = contentVariables;

    if (!time) {
      throw new Error("[dueBeforeRuleInflectionPoint] [tomorrowAtTime] time content variable is required");
    }

    const todayDayAtTime = notificationDue.set({ hour: time.hours, minute: time.minutes }).startOf("minute");

    if (notificationDue < todayDayAtTime) {
      const yesterdayDayAtTime = notificationDue.minus({ days: 1 }).startOf("day");
      return yesterdayDayAtTime;
    }

    return notificationDue.startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before this week"
  // - given that a week starts on "Monday"
  // - template: "this week"
  // - reindex on Mon, 3/31 at 12am
  //   - interpreted as due before Mon, 3/31 at 12am
  thisWeek: (props) => {
    const { notificationDue } = props;
    const startOfWeek = getStartOfWeek(props.weekStartsOn, notificationDue);
    return startOfWeek.plus({ weeks: 1 });
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before this weekend"
  // - given that a weekend starts on "Saturday"
  // - template: "this weekend"
  // - reindex on Mon, 3/31 at 12am
  //   - interpreted as due before Sat, 4/5 at 12am
  thisWeekend: (props) => {
    const { notificationDue } = props;

    // If the notification is due on the weekend then the inflection point is the upcoming
    // Monday. Else the inflection point is the previous Monday.
    switch (notificationDue.weekday) {
      case Weekday.SATURDAY:
        return notificationDue.plus({ weeks: 1 }).startOf("day");
      case Weekday.SUNDAY:
        return notificationDue.minus({ days: 1 }).plus({ weeks: 1 }).startOf("day");
      default: {
        const daysSinceMonday = getDaysSinceWeekday("mo", notificationDue);
        return notificationDue.minus({ days: daysSinceMonday }).startOf("day");
      }
    }
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before this month"
  // - template: "this month"
  // - reindex on Tue, 4/1 at 12am
  //   - interpreted as due before Tue, 4/1 at 12am
  thisMonth: (props) => {
    const { notificationDue } = props;
    return notificationDue.plus({ months: 1 }).startOf("month");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before this year"
  // - template: "this year"
  // - reindex on Jan 1 of next year at 12am
  thisYear: (props) => {
    const { notificationDue } = props;
    return notificationDue.plus({ years: 1 }).startOf("year");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before next week"
  // - given that a week starts on "Monday"
  // - template: "next week"
  // - reindex on Mon, 3/24 at 12am
  //   - interpreted as due before Mon, 3/31 at 12am
  nextWeek: (props) => {
    const { notificationDue } = props;
    const startOfWeek = getStartOfWeek(props.weekStartsOn, notificationDue);
    return startOfWeek.startOf("day");
  },

  // // - notification due on Sun, 3/30 & 5pm
  // // - "due before next weekend"
  // // - given that a weekend starts on "Saturday"
  // // - template: "next weekend"
  // // - reindex on Mon, 3/24 at 12am
  // //   - interpreted as due before Sat, 4/5 at 12am
  // nextWeekend: (props) => {
  //   const { notificationDue } = props;
  //   // const startOfWeekend = getStartOfWeekend(props.weekendStartsOn, notificationDue);
  //   // return startOfWeekend.plus({ weeks: 1 }).startOf("day");

  //   // If the notification is due on the weekend then the inflection point is the previous
  //   // Monday. Else the inflection point is the next Monday.
  //   switch (notificationDue.weekday) {
  //     case Weekday.SATURDAY:
  //     case Weekday.SUNDAY: {
  //       const daysSinceMonday = getDaysSinceWeekday("mo", notificationDue);
  //       return notificationDue.minus({ days: daysSinceMonday }).startOf("day");
  //     }
  //     default: {
  //       const daysSinceMonday = getDaysSinceWeekday("mo", notificationDue);
  //       return notificationDue.minus({ days: daysSinceMonday }).startOf("day");
  //     }
  //   }
  // },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before next month"
  // - template: "next month"
  // - reindex on Tue, 3/1 at 12am
  //   - interpreted as due before Tue, 4/1 at 12am
  nextMonth: (props) => {
    const { notificationDue } = props;
    return notificationDue.startOf("month");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before next year"
  // - template: "next year"
  // - reindex on Jan 1 of the current year
  nextYear: (props) => {
    const { notificationDue } = props;
    return notificationDue.startOf("year");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before an hour from now"
  // - template: "an hour from now"
  // - reindex on Sun, 3/30 at 4:00:00:001pm
  //   - interpreted as due before 3/30 at 5:00:00:001pm
  anHourFromNow: (props) => {
    const { notificationDue } = props;
    return notificationDue.minus({ hours: 1 }).plus({ milliseconds: 1 });
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before a day from now"
  // - template: "a day from now"
  // - reindex on Sat, 3/29 at 5:00:00:001pm
  //   - interpreted as due before 3/30 at 5:00:00:001pm
  aDayFromNow: (props) => {
    const { notificationDue } = props;
    return notificationDue.minus({ days: 1 }).plus({ milliseconds: 1 });
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before a week from now"
  // - template: "a week from now"
  // - reindex on Sun, 3/24 at 12am
  //   - interpreted as due before 3/31 at 12am
  aWeekFromNow: (props) => {
    const { notificationDue } = props;
    // If due date is 3/30 at 5pm, and the rule is "a week from now",
    // the inflection point is 3/24 at 12am (one week before 3/31 at 12am)
    return notificationDue.minus({ weeks: 1 }).plus({ days: 1 }).startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before a month from now"
  // - template: "a month from now"
  // - reindex on Wed, 3/1 at 12am
  //   - interpreted as due before 4/1 at 12am
  //   - note that we need to reindex on 3/1 because February only has 28
  //     days and adding a month to 2/28 will result in 3/28.
  aMonthFromNow: (props) => {
    const { notificationDue } = props;
    // Get the start of the current month (3/1 for a notification due on 3/30)
    return notificationDue.minus({ months: 1 }).plus({ days: 1 }).startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before 3 hours from now"
  // - template: "{{NUMBER}} hour(s) from now"
  // - reindex on Sun, 3/30 at 2:00:00:001pm
  //   - interpreted as due before 3/30 at 5:00:00:001pm
  xHoursFromNow: (props) => {
    const { notificationDue, contentVariables } = props;
    const { amount } = contentVariables;

    if (!amount) {
      throw new Error("[dueBeforeRuleInflectionPoint] [xHoursFromNow] amount content variable is required");
    }

    return notificationDue.minus({ hours: amount }).plus({ milliseconds: 1 });
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before 3 days from now"
  // - template: "{{NUMBER}} day(s) from now"
  // - reindex on Fri, 3/28 at 12am
  //   - interpreted as due before 3/31 at 12am
  //   - note that if this rule is "1" day from now, it will be interpreted
  //     as simply adding 1 day to the current datetime and keeping the same
  //     time. If this rule is more than 1 day from now, it will be interpreted
  //     as adding the specified number of days to the current datetime and
  //     setting the time to 12am.
  xDaysFromNow: (props) => {
    const { notificationDue, contentVariables } = props;
    const { amount } = contentVariables;

    if (!amount) {
      throw new Error("[dueBeforeRuleInflectionPoint] [xDaysFromNow] amount content variable is required");
    }

    if (amount === 1) {
      // For "1 day from now", we keep the same time
      return notificationDue.minus({ days: 1 }).plus({ milliseconds: 1 });
    } else {
      // For multiple days, the date is normalized to midnight
      // So for "3 days from now" with due date on 3/30, we find when it would change
      // to "before 3/31", which would be on 3/28 at 12am
      return notificationDue.minus({ days: amount }).plus({ days: 1 }).startOf("day");
    }
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due before 2 weeks from now"
  // - template: "{{NUMBER}} week(s) from now"
  // - reindex on Mon, 3/17 at 12am
  //   - interpreted as due before 3/31 at 12am
  xWeeksFromNow: (props) => {
    const { notificationDue, contentVariables } = props;
    const { amount } = contentVariables;

    if (!amount) {
      throw new Error("[dueBeforeRuleInflectionPoint] [xWeeksFromNow] amount content variable is required");
    }

    // For "2 weeks from now" with due date on 3/30, we find when it would change
    // to "before 3/31", which would be on 3/17 at 12am
    return notificationDue.minus({ weeks: amount }).plus({ days: 1 }).startOf("day");
  },

  // // - notification due on Sun, 3/30 & 5pm
  // // - "due before 2 months from now"
  // // - template: "{{NUMBER}} month(s) from now"
  // // - reindex on Fri, 1/31 at 12am
  // //   - interpreted as due before 3/31 at 12am
  // xMonthsFromNow: (props) => {
  //   const { notificationDue, contentVariables } = props;
  //   const { amount } = contentVariables;

  //   if (!amount) {
  //     throw new Error("[dueBeforeRuleInflectionPoint] [xMonthsFromNow] amount content variable is required");
  //   }

  //   const target = notificationDue.minus({ months: amount }).plus({ days: 1 }).startOf("day");

  //   // scenario
  //   // If the due date is 2/28 and the rule is "2 month from now",
  //   // the inflection point is 1/1. This is because 2 months before is 12/28.
  //   // Adding 2 months to 12/28 is 2/28. Adding 2 months 12/29 or 12/30 or 12/31
  //   // is still 2/28 (and 2/28 is not before a notification due on 2/28).
  //   if (notificationDue < target.plus({ months: amount })) {
  //     return target;
  //   }

  //   return target.plus({ months: 1 }).startOf("month");
  // },
} satisfies Record<keyof typeof dueDateRuleSuggestions, DueDateRuleInflectionPointResolver>;

export const dueOnOrAfterRuleInflectionPoint = {
  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after today"
  // - template: "today"
  // - reindex on Mon, 3/31 at 12am
  //   - interpreted as due on or after Mon, 3/31 at 12am (no longer Sun, 3/30)
  today: (props) => {
    const { notificationDue } = props;
    return notificationDue.plus({ days: 1 }).startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after today at 3pm"
  // - template: "today at {{NUMBER}}" coerced/normalized to "today at {{TIME}}"
  // - reindex on Mon, 3/31 at 12am
  //   - interpreted as due on or after Mon, 3/31 at 3pm (no longer Sun, 3/30 at 3pm)
  todayAtNumber: (props) => {
    const { time } = props.contentVariables;

    if (!time) {
      throw new Error("[dueOnOrAfterRuleInflectionPoint] [todayAtNumber] time content variable is required");
    }

    // We need this type assertion so that the type doesn't reference itself
    const resolver = dueOnOrAfterRuleInflectionPoint.todayAtTime as DueDateRuleInflectionPointResolver;
    return resolver(props);
  },
  // - template: "today at {{TIME}}"
  todayAtTime: (props) => {
    const { notificationDue, contentVariables } = props;
    const { time } = contentVariables;

    if (!time) {
      throw new Error("[dueOnOrAfterRuleInflectionPoint] [todayAtTime] time content variable is required");
    }

    const sameDayAtTime = notificationDue.set({ hour: time.hours, minute: time.minutes }).startOf("minute");

    if (notificationDue >= sameDayAtTime) return notificationDue.plus({ days: 1 }).startOf("day");

    return notificationDue.startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after tomorrow"
  // - template: "tomorrow"
  // - reindex on Sun, 3/30 at 12am
  //   - interpreted as due on or after Mon, 3/31 at 12am (no longer Sun, 3/30)
  tomorrow: (props) => {
    const { notificationDue } = props;
    return notificationDue.startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after tomorrow at 3pm"
  // - template: "tomorrow at {{NUMBER}}" coerced/normalized to "tomorrow at {{TIME}}"
  // - reindex on Sun, 3/30 at 12am
  //   - interpreted as due on or after Mon, 3/31 at 3pm (no longer Sun, 3/30)
  tomorrowAtNumber: (props) => {
    const { time } = props.contentVariables;

    if (!time) {
      throw new Error("[dueOnOrAfterRuleInflectionPoint] [tomorrowAtNumber] time content variable is required");
    }

    // We need this type assertion so that the type doesn't reference itself
    const resolver = dueOnOrAfterRuleInflectionPoint.tomorrowAtTime as DueDateRuleInflectionPointResolver;
    return resolver(props);
  },
  // - template: "tomorrow at {{TIME}}"
  tomorrowAtTime: (props) => {
    const { notificationDue, contentVariables } = props;
    const { time } = contentVariables;

    if (!time) {
      throw new Error("[dueOnOrAfterRuleInflectionPoint] [tomorrowAtTime] time content variable is required");
    }

    // Get today at the specified time
    const sameDayAtTime = notificationDue.set({ hour: time.hours, minute: time.minutes }).startOf("minute");

    if (notificationDue >= sameDayAtTime) {
      return notificationDue.startOf("day");
    }

    return notificationDue.minus({ days: 1 }).startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after this week"
  // - given that a week starts on "Monday"
  // - template: "this week"
  // - reindex on Mon, 3/31 at 12am
  //   - interpreted as due on or after new week starting Mon, 3/31 (no longer week of 3/24)
  thisWeek: (props) => {
    const { notificationDue } = props;
    const startOfWeek = getStartOfWeek(props.weekStartsOn, notificationDue);
    return startOfWeek.plus({ weeks: 1 }).startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after this weekend"
  // - given that a weekend starts on "Saturday"
  // - template: "this weekend"
  // - reindex on Mon, 3/24 at 12am
  //   - interpreted as due on or after Sat, 3/29 at 12am
  thisWeekend: (props) => {
    const { notificationDue } = props;

    switch (notificationDue.weekday) {
      case Weekday.SATURDAY:
        return notificationDue.plus({ weeks: 1 }).startOf("day");
      case Weekday.SUNDAY:
        return notificationDue.minus({ days: 1 }).plus({ weeks: 1 }).startOf("day");
      default: {
        const daysSinceMonday = getDaysSinceWeekday("mo", notificationDue);
        return notificationDue.minus({ days: daysSinceMonday }).startOf("day");
      }
    }
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after this month"
  // - template: "this month"
  // - reindex on Tue, 4/1 at 12am
  //   - interpreted as due on or after April (no longer March)
  thisMonth: (props) => {
    const { notificationDue } = props;
    return notificationDue.plus({ months: 1 }).startOf("month");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after this year"
  // - template: "this year"
  // - reindex on Jan 1 of next year at 12am
  thisYear: (props) => {
    const { notificationDue } = props;
    return notificationDue.plus({ years: 1 }).startOf("year");
  },

  // // - notification due on Sun, 3/30 & 5pm
  // // - "due on or after next weekend"
  // // - given that a weekend starts on "Saturday"
  // // - template: "next weekend"
  // // - reindex on Sat, 3/29 at 12am
  // //   - interpreted as due on or after weekend of 4/5 (no longer weekend of 3/29)
  // //   - note that "this weekend" and "next weekend" are interpreted as the same thing
  // nextWeekend: (props) => {
  //   const { notificationDue } = props;
  //   const startOfWeekend = getStartOfWeekend(props.weekendStartsOn, notificationDue);
  //   return startOfWeekend.plus({ weeks: 1 }).startOf("day");
  // },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after next week"
  // - given that a week starts on "Monday"
  // - template: "next week"
  // - reindex on Mon, 3/24 at 12am
  //   - interpreted as due on or after week of 3/31 (no longer week of 3/24)
  nextWeek: (props) => {
    const { notificationDue } = props;
    const startOfWeek = getStartOfWeek(props.weekStartsOn, notificationDue);
    return startOfWeek;
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after next month"
  // - template: "next month"
  // - reindex on 3/31 at 12am
  //   - interpreted as due on or after April (no longer March)
  nextMonth: (props) => {
    const { notificationDue } = props;

    // For "next month", the inflection point occurs at the start of the current month
    return notificationDue.startOf("month");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after next year"
  // - template: "next year"
  // - reindex on Jan 1 of the current year
  nextYear: (props) => {
    const { notificationDue } = props;
    return notificationDue.startOf("year");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after an hour from now"
  // - template: "an hour from now"
  // - reindex on Sun, 3/30 at 4:00:00:001pm
  //   - interpreted as due on or after Sun, 3/30 at 5:00:00:001pm
  anHourFromNow: (props) => {
    const { notificationDue } = props;
    return notificationDue.minus({ hours: 1 }).plus({ milliseconds: 1 });
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after a day from now"
  // - template: "a day from now"
  // - reindex on Sat, 3/29 at 5:00:00:001pm
  //   - interpreted as due on or after Sun, 3/30 at 5:00:00:001pm
  aDayFromNow: (props) => {
    const { notificationDue } = props;
    return notificationDue.minus({ days: 1 }).plus({ milliseconds: 1 });
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after a week from now"
  // - template: "a week from now"
  // - reindex on Mon, 3/24 at 12am
  //   - interpreted as due on or after Mon, 3/31 at 12am (no longer Sun, 3/30)
  aWeekFromNow: (props) => {
    const { notificationDue } = props;
    // For "a week from now" with notification due on 3/30 at 5pm,
    // the inflection point is when the rule starts pointing to 3/31 at 12am
    return notificationDue.minus({ weeks: 1 }).plus({ days: 1 }).startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after a month from now"
  // - template: "a month from now"
  // - reindex on 3/1 at 12am
  //   - interpreted as due on or after 4/1 at 12am (no longer Sun, 3/30)
  //   - note that we need to reindex on 3/1 because February only has 28
  //     days and adding a month to 2/28 will result in 3/28.
  aMonthFromNow: (props) => {
    const { notificationDue } = props;

    // For "a month from now" with notification due on 3/30 at 5pm,
    // the inflection point is when the rule starts pointing to 4/1 at 12am
    // So we need to find the date that is 1 month before 4/1
    return notificationDue.minus({ months: 1 }).plus({ days: 1 }).startOf("day");
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after 3 hours from now"
  // - template: "{{NUMBER}} hour(s) from now"
  // - reindex on Sun, 3/30 at 2:00:00:001pm
  //   - interpreted as due on or after Sun, 3/30 at 5:00:00:001pm (no longer 5pm)
  xHoursFromNow: (props) => {
    const { notificationDue, contentVariables } = props;
    const { amount } = contentVariables;

    if (!amount) {
      throw new Error("[dueOnOrAfterRuleInflectionPoint] [xHoursFromNow] amount content variable is required");
    }

    return notificationDue.minus({ hours: amount }).plus({ milliseconds: 1 });
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after 3 days from now"
  // - template: "{{NUMBER}} day(s) from now"
  // - reindex on Fri, 3/28 at 12am
  //   - interpreted as due on or after Mon, 3/31 at 12am (no longer Sun, 3/30)
  //   - note that if this rule is "1" day from now, it will be interpreted
  //     as simply adding 1 day to the current datetime and keeping the same
  //     time. If this rule is more than 1 day from now, it will be interpreted
  //     as adding the specified number of days to the current datetime and
  //     setting the time to 12am.
  xDaysFromNow: (props) => {
    const { notificationDue, contentVariables } = props;
    const { amount } = contentVariables;

    if (!amount) {
      throw new Error("[dueOnOrAfterRuleInflectionPoint] [xDaysFromNow] amount content variable is required");
    }

    if (amount === 1) {
      // For "1 day from now", we keep the same time
      return notificationDue.minus({ days: 1 }).plus({ milliseconds: 1 });
    } else {
      // For multiple days, the date is normalized to midnight
      return notificationDue.minus({ days: amount }).plus({ days: 1 }).startOf("day");
    }
  },

  // - notification due on Sun, 3/30 & 5pm
  // - "due on or after 2 weeks from now"
  // - template: "{{NUMBER}} week(s) from now"
  // - reindex on 3/17 at 12am
  //   - interpreted as due on or after 3/31 at 12am (no longer Sun, 3/30)
  xWeeksFromNow: (props) => {
    const { notificationDue, contentVariables } = props;
    const { amount } = contentVariables;

    if (!amount) {
      throw new Error("[dueOnOrAfterRuleInflectionPoint] [xWeeksFromNow] amount content variable is required");
    }

    // For "2 weeks from now" with notification due on 3/30 at 5pm,
    // the inflection point is when the rule starts pointing to 3/31 at 12am
    return notificationDue.minus({ weeks: amount }).plus({ days: 1 }).startOf("day");
  },

  // // - notification due on Sun, 3/30 & 5pm
  // // - "due on or after 2 months from now"
  // // - template: "{{NUMBER}} month(s) from now"
  // // - reindex on 1/31 at 12am
  // //   - interpreted as due on or after 3/31 at 12am (no longer Sun, 3/30)
  // xMonthsFromNow: (props) => {
  //   const { notificationDue, contentVariables } = props;
  //   const { amount } = contentVariables;

  //   if (!amount) {
  //     throw new Error("[dueOnOrAfterRuleInflectionPoint] [xMonthsFromNow] amount content variable is required");
  //   }

  //   const target = notificationDue.minus({ months: amount }).plus({ days: 1 }).startOf("day");

  //   // Handle edge cases similar to the "due before" implementation
  //   if (notificationDue >= target.plus({ months: amount })) {
  //     return target.plus({ months: 1 }).startOf("month");
  //   }

  //   return target;
  // },
} satisfies Record<keyof typeof dueDateRuleSuggestions, DueDateRuleInflectionPointResolver>;
